import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { format, startOfDay, endOfDay, subDays, addDays, subHours } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, X, Video, VideoOff, SkipForward } from 'lucide-react';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import { useSearchParams } from 'react-router-dom';
import useStore from '../stores/useStore';
import useAuthStore from '../stores/authStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import WebRTCPlayer from '../components/WebRTCPlayer';
import DigitalZoom from '../components/DigitalZoom';
import MirasysPlayer from '../components/MirasysPlayer';
import LazyThumbnail from '../components/LazyThumbnail';
import MilestonePlayer from '../components/MilestonePlayer';
import GenetecPlayer from '../components/GenetecPlayer';
import PtzControls from '../components/PtzControls';
import { CalendarPicker, TimePicker } from '../components/DateTimePicker';

const ArchivePlaybackNew = () => {
  // Core state
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [playbackTime, setPlaybackTime] = useState(new Date());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  
  // Data state
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [motionFootage, setMotionFootage] = useState([]);
  const [analyticsFootage, setAnalyticsFootage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genetecStreamConfig, setGenetecStreamConfig] = useState(null);
  
  // UI state
  const [cameraSearch, setCameraSearch] = useState('');
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.25); // Default to 30m view
  const [filters, setFilters] = useState({ recordings: true, motion: true, analytics: true });
  
  // Timeline dragging and pinch zoom
  const [isDragging, setIsDragging] = useState(false);
  const timelineRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0);

  const isPinching = useRef(false);
  
  const { cameras } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantVersion, globalConfig } = useAuthStore();
  
  // Timeline config based on zoom
  const getTimelineConfig = (zoom) => {
    if (zoom <= 0.0028) return { hours: 10/3600, pxPerMin: 3600 };
    if (zoom <= 0.0083) return { hours: 30/3600, pxPerMin: 1200 };
    if (zoom <= 0.0167) return { hours: 1/60, pxPerMin: 600 };
    if (zoom <= 0.0833) return { hours: 5/60, pxPerMin: 120 };
    if (zoom <= 0.25) return { hours: 0.5, pxPerMin: 24 };
    if (zoom <= 0.5) return { hours: 1, pxPerMin: 12 };
    return { hours: 2, pxPerMin: 6 };
  };
  const { hours: TIMELINE_HOURS, pxPerMin: PIXELS_PER_MINUTE } = getTimelineConfig(zoomLevel);
  
  // Load cameras on mount
  useEffect(() => {
    loadCameras();
  }, [tenantVersion, globalConfig]);

  // Auto-select camera from URL param (e.g. /cameras?camera=xxx)
  useEffect(() => {
    const cameraParam = searchParams.get('camera');
    if (cameraParam && cameras.length > 0 && !selectedCamera) {
      const match = cameras.find(c => c.id === cameraParam);
      if (match) {
        setSelectedCamera(match);
        setIsLiveMode(true);
        searchParams.delete('camera');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [cameras, searchParams]);
  
  // Fetch Genetec stream config when a Genetec camera is selected
  useEffect(() => {
    if (selectedCamera?.integrationType === 'GENETEC_VMS' && selectedCamera?.integrationId) {
      setGenetecStreamConfig(null);
      fetchWithTenant(
        '/api/vms/genetec/' + selectedCamera.integrationId + '/stream-config/' + encodeURIComponent(selectedCamera.id)
      )
        .then(r => r.json())
        .then(config => setGenetecStreamConfig(config))
        .catch(err => console.error('[Cameras] Genetec stream config error:', err));
    } else {
      setGenetecStreamConfig(null);
    }
  }, [selectedCamera?.id]);

  const loadCameras = async () => {
    try {
      const [camerasRes, layoutsRes] = await Promise.all([
        fetchWithTenant('/api/vms/cameras'),
        fetchWithTenant('/api/wave/layouts')
      ]);
      
      if (camerasRes.ok) {
        const data = await camerasRes.json();
        useStore.getState().setCameras(data.cameras || data || []);
      }
      
      if (layoutsRes.ok) {
        const data = await layoutsRes.json();
        setLayouts(data.layouts || data || []);
      }
    } catch (error) {
      console.error('Failed to load cameras/layouts:', error);
    }
  };
  
  // Load recordings when camera or date changes
  useEffect(() => {
    if (selectedCamera && selectedDate) {
      loadRecordings();
    }
  }, [selectedCamera?.id, selectedDate?.toDateString()]);
  
  const getVmsFamily = (camera) => {
    if (!camera) return 'unknown';
    const it = camera.integrationType || '';
    if (it === 'GENETEC_VMS') return 'genetec';
    if (it === 'MILESTONE_VMS' || camera.vmsType === 'MILESTONE_VMS') return 'milestone';
    if (it === 'MIRASYS_VMS' || camera.metadata?.vmsType === 'mirasys') return 'mirasys';
    if (it === 'WAVE_VMS' || !it) return 'wave';
    return 'unknown';
  };

  const loadRecordings = async () => {
    if (!selectedCamera) return;
    setLoading(true);
    try {
      const dayStart = startOfDay(selectedDate).getTime();
      const dayEnd = endOfDay(selectedDate).getTime();
      
      // Route recordings to the correct VMS
      const family = getVmsFamily(selectedCamera);
      const camId = encodeURIComponent(selectedCamera.id);
      let apiUrl;
      if (family === 'wave') {
        apiUrl = `/api/wave/recordings?cameraId=${camId}&startTime=${dayStart}&endTime=${dayEnd}`;
      } else {
        apiUrl = `/api/vms/cameras/${camId}/recordings?startTime=${dayStart}&endTime=${dayEnd}`;
      }
      
      const response = await fetchWithTenant(apiUrl);
      if (response.ok) {
        const data = await response.json();
        let periods = [];
        
        // Handle different response formats
        if (data?.recordings && Array.isArray(data.recordings)) {
          // VMS API format: { recordings: [...] }
          periods = data.recordings.map(p => {
            const start = p.startTimeMs ? parseInt(p.startTimeMs) : (typeof p.startTime === 'string' && p.startTime.includes('T') ? new Date(p.startTime).getTime() : parseInt(p.startTime));
            const end = p.durationMs ? start + parseInt(p.durationMs) : (p.endTime ? (typeof p.endTime === 'string' && p.endTime.includes('T') ? new Date(p.endTime).getTime() : parseInt(p.endTime)) : start);
            return { startTimeMs: start, durationMs: end - start };
          });
        } else if (data?.reply?.[0]?.periods) {
          // Wave API format: { reply: [{ periods: [...] }] }
          const now = Date.now();
          periods = data.reply[0].periods.map(p => {
            const startTimeMs = parseInt(p.startTimeMs);
            let durationMs = parseInt(p.durationMs);
            if (durationMs < 0) {
              durationMs = now - startTimeMs;
            }
            return { startTimeMs, durationMs };
          });
        } else if (Array.isArray(data)) {
          periods = data.map(p => ({
            startTimeMs: parseInt(p.startTimeMs || p.startTime),
            durationMs: parseInt(p.durationMs || p.duration)
          }));
        }
        
        setRecordings(periods);
        
        // Also fetch motion and analytics (skip for Mirasys)
        if (family === 'wave') {
          if (family === 'wave') {
            fetchFootageByType(selectedCamera.id, dayStart, dayEnd, 'motion').then(setMotionFootage);
            fetchFootageByType(selectedCamera.id, dayStart, dayEnd, 'analytics').then(setAnalyticsFootage);
          } else {
            setMotionFootage([]);
            setAnalyticsFootage([]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load recordings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFootageByType = async (cameraId, startTime, endTime, periodType) => {
    try {
      const response = await fetchWithTenant(
        `/api/wave/footage?cameraId=${encodeURIComponent(cameraId)}&startTime=${startTime}&endTime=${endTime}&periodType=${periodType}`
      );
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          return data.map(chunk => ({
            startTimeMs: parseInt(chunk.startTimeMs),
            durationMs: parseInt(chunk.durationMs),
            type: periodType
          }));
        }
      }
    } catch (error) {
      console.error(`Failed to load ${periodType} footage:`, error);
    }
    return [];
  };
  
  // Handle tap to show/hide controls
  const handleVideoTap = () => {
    setShowControls(true);
    
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    // Hide controls after 3 seconds if playing
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 8000);
    }
  };
  
  // Reset controls timer when interacting with timeline
  const handleTimelineInteraction = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 8000);
    }
  };

  // Auto-hide controls when video starts playing
  useEffect(() => {
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 8000);
    } else {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  // Update playback time during playback
  useEffect(() => {
    let interval = null;
    
    if (isLiveMode) {
      // In live mode, update time every second to show current time
      interval = setInterval(() => {
        setPlaybackTime(new Date());
      }, 1000);
    } else if (isPlaying && !isLiveMode) {
      // In archive mode while playing, advance time at 1x speed
      interval = setInterval(() => {
        setPlaybackTime(prev => {
          const newTime = new Date(prev.getTime() + 1000);
          // Don't go past current time
          const now = new Date();
          if (newTime > now) {
            setIsLiveMode(true);
            return now;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, isLiveMode]);


  // Camera selection
  const handleCameraSelect = (camera) => {
    setSelectedCamera(camera);
    setIsPlaying(false);
    setIsLiveMode(true);
    setPlaybackTime(new Date());
    setSelectedDate(new Date());
  };
  
  const handleCloseCamera = () => {
    setSelectedCamera(null);
    setIsPlaying(false);
  };
  
  // Playback controls
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  
  const goToLive = () => {
    const now = new Date();
    setPlaybackTime(now);
    setSelectedDate(now);
    setIsLiveMode(true);
    setIsPlaying(false);
  };
  
  const goToRecorded = (time) => {
    setPlaybackTime(time);
    setSelectedDate(time);
    setIsLiveMode(false);
    setIsPlaying(false);
  };
  
  // Timeline dragging
  const handleTimelineMouseDown = (e) => {
    // Ignore multi-touch (handled by pinch zoom)
    if (e.touches && e.touches.length > 1) return;
    
    // Don't start dragging if pinching
    if (isPinching.current) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setIsDragging(true);
    dragStartX.current = clientX;
    dragStartTime.current = playbackTime.getTime();
  };
  

  
  const handleTimelineMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragStartX.current;
    const deltaMs = (deltaX / PIXELS_PER_MINUTE) * 60 * 1000;
    const newTime = new Date(dragStartTime.current - deltaMs);
    
    const now = new Date();
    if (newTime > now) {
      setPlaybackTime(now);
    } else {
      setPlaybackTime(newTime);
      if (newTime.toDateString() !== selectedDate.toDateString()) {
        setSelectedDate(newTime);
      }
    }
    setIsLiveMode(false);
    setIsPlaying(false);
  }, [isDragging, PIXELS_PER_MINUTE, selectedDate]);
  
  const handleTimelineMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleTimelineMouseMove);
      window.addEventListener('mouseup', handleTimelineMouseUp);
      window.addEventListener('touchmove', handleTimelineMouseMove);
      window.addEventListener('touchend', handleTimelineMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleTimelineMouseMove);
      window.removeEventListener('mouseup', handleTimelineMouseUp);
      window.removeEventListener('touchmove', handleTimelineMouseMove);
      window.removeEventListener('touchend', handleTimelineMouseUp);
    };
  }, [isDragging, handleTimelineMouseMove, handleTimelineMouseUp]);
  
  // Pinch zoom on timeline
  const pinchRef = useRef({ initialDistance: 0, initialZoom: 0.25 });
  
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;
    
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        isPinching.current = true;
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        pinchRef.current.initialDistance = Math.hypot(
          touch2.clientX - touch1.clientX, 
          touch2.clientY - touch1.clientY
        );
        pinchRef.current.initialZoom = zoomLevel;
        console.log('[PINCH] Start, distance:', pinchRef.current.initialDistance);
      }
    };
    
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && isPinching.current) {
        e.preventDefault();
        e.stopPropagation();
        
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX, 
          touch2.clientY - touch1.clientY
        );
        
        if (pinchRef.current.initialDistance > 0) {
          const scale = currentDistance / pinchRef.current.initialDistance;
          const newZoom = Math.min(1, Math.max(0.0028, pinchRef.current.initialZoom / scale));
          console.log('[PINCH] Move, scale:', scale.toFixed(2), 'newZoom:', newZoom.toFixed(4));
          setZoomLevel(newZoom);
        }
      }
    };
    
    const onTouchEnd = (e) => {
      if (isPinching.current) {
        console.log('[PINCH] End');
        isPinching.current = false;
        pinchRef.current.initialDistance = 0;
      }
    };
    
    // Prevent default gestures on the timeline
    const preventGesture = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };
    
    timeline.addEventListener('touchstart', onTouchStart, { passive: false });
    timeline.addEventListener('touchmove', onTouchMove, { passive: false });
    timeline.addEventListener('touchend', onTouchEnd);
    timeline.addEventListener('gesturestart', preventGesture);
    timeline.addEventListener('gesturechange', preventGesture);
    timeline.addEventListener('gestureend', preventGesture);
    
    return () => {
      timeline.removeEventListener('touchstart', onTouchStart);
      timeline.removeEventListener('touchmove', onTouchMove);
      timeline.removeEventListener('touchend', onTouchEnd);
      timeline.removeEventListener('gesturestart', preventGesture);
      timeline.removeEventListener('gesturechange', preventGesture);
      timeline.removeEventListener('gestureend', preventGesture);
    };
  }, [zoomLevel]);
  
  // Generate timeline markers
  const generateTimeMarkers = () => {
    const markers = [];
    const centerTime = playbackTime.getTime();
    const visibleMs = TIMELINE_HOURS * 60 * 60 * 1000;
    const startMs = centerTime - visibleMs / 2;
    const endMs = centerTime + visibleMs / 2;
    
    let intervalMs;
    if (zoomLevel <= 0.0028) intervalMs = 1000;
    else if (zoomLevel <= 0.0083) intervalMs = 5000;
    else if (zoomLevel <= 0.0167) intervalMs = 10000;
    else if (zoomLevel <= 0.0833) intervalMs = 30000;
    else if (zoomLevel <= 0.25) intervalMs = 60000;
    else if (zoomLevel <= 0.5) intervalMs = 300000;
    else intervalMs = 600000;
    
    let markerTime = Math.floor(startMs / intervalMs) * intervalMs;
    while (markerTime <= endMs) {
      const markerDate = new Date(markerTime);
      const offsetFromCenter = (markerTime - centerTime) / 60000 * PIXELS_PER_MINUTE;
      const mins = markerDate.getMinutes();
      const secs = markerDate.getSeconds();
      const isHour = mins === 0 && secs === 0;
      const isMajor = secs === 0 && (mins === 0 || mins === 30);
      markers.push({ time: markerDate, offset: offsetFromCenter, isHour, isMajor });
      markerTime += intervalMs;
    }
    return markers;
  };
  
  // Generate activity bars
  const generateActivityBars = () => {
    const bars = [];
    const centerTime = playbackTime.getTime();
    const visibleMs = TIMELINE_HOURS * 60 * 60 * 1000;
    const visibleStart = centerTime - visibleMs / 2;
    const visibleEnd = centerTime + visibleMs / 2;
    
    const addBars = (footage, type, keyPrefix) => {
      if (!filters[type]) return;
      footage.forEach((chunk, idx) => {
        const startMs = chunk.startTimeMs;
        const endMs = startMs + chunk.durationMs;
        
        if (endMs >= visibleStart && startMs <= visibleEnd) {
          const clampedStart = Math.max(startMs, visibleStart);
          const clampedEnd = Math.min(endMs, visibleEnd);
          const startOffsetPx = ((clampedStart - centerTime) / 60000) * PIXELS_PER_MINUTE;
          const endOffsetPx = ((clampedEnd - centerTime) / 60000) * PIXELS_PER_MINUTE;
          const widthPx = Math.max(2, endOffsetPx - startOffsetPx);
          
          bars.push({ key: `${keyPrefix}-${idx}`, offset: startOffsetPx, width: widthPx, type });
        }
      });
    };
    
    addBars(recordings, 'recordings', 'rec');
    addBars(motionFootage, 'motion', 'mot');
    addBars(analyticsFootage, 'analytics', 'ana');
    
    return bars;
  };

  // Check if current playback time falls within a recording period
  const isInRecordingPeriod = useCallback((timeMs) => {
    if (!recordings || recordings.length === 0) return false;
    return recordings.some(p => {
      const start = p.startTimeMs;
      const end = start + p.durationMs;
      return timeMs >= start && timeMs <= end;
    });
  }, [recordings]);

  // Find the nearest recording segment to a given time
  const findNearestRecording = useCallback((timeMs) => {
    if (!recordings || recordings.length === 0) return null;
    let nearest = null;
    let minDistance = Infinity;
    for (const p of recordings) {
      const start = p.startTimeMs;
      const end = start + p.durationMs;
      // If inside a period, return it
      if (timeMs >= start && timeMs <= end) return { time: timeMs, direction: 'current' };
      // Check distance to start of this period (next recording)
      const distToStart = Math.abs(start - timeMs);
      if (distToStart < minDistance) {
        minDistance = distToStart;
        nearest = { time: start, direction: start > timeMs ? 'next' : 'previous' };
      }
      // Check distance to end of this period (end of previous recording)
      const distToEnd = Math.abs(end - timeMs);
      if (distToEnd < minDistance) {
        minDistance = distToEnd;
        nearest = { time: end, direction: end > timeMs ? 'next' : 'previous' };
      }
    }
    return nearest;
  }, [recordings]);

  const hasRecordingAtPlayback = isLiveMode || isInRecordingPeriod(playbackTime.getTime());

  const jumpToNearestRecording = () => {
    const nearest = findNearestRecording(playbackTime.getTime());
    if (nearest) {
      setPlaybackTime(new Date(nearest.time));
    }
  };

  
  const formatTimeDisplay = (date) => format(date, 'HH:mm:ss');
  
  // Filter cameras based on search and layout
  const filteredCameras = cameras.filter(camera => {
    const matchesSearch = camera.name.toLowerCase().includes(cameraSearch.toLowerCase());
    const matchesLayout = !selectedLayout || (selectedLayout.items || []).some(item => item.resourceId === camera.id);
    return matchesSearch && matchesLayout;
  });

  // Camera grid view
  if (!selectedCamera) {
    return (
      <div className="archive-new">
        <div className="archive-camera-select">
          <CcmLicenseBanner />
          <div className="page-header-with-actions">
            <div>
              <h1 className="page-title">
                <Video size={24} />
                Cameras
              </h1>
              <p className="page-subtitle">Live view and recorded footage</p>
            </div>
            <div className="camera-select-controls">
              <div className="camera-search-wrapper">
              <input
                type="text"
                className="camera-search-input"
                placeholder="Search cameras..."
                value={cameraSearch}
                onChange={(e) => setCameraSearch(e.target.value)}
                autoComplete="off"
              />
              {cameraSearch && (
                <button 
                  className="camera-search-clear" 
                  onClick={() => setCameraSearch('')}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {layouts.length > 0 && (
              <select 
                className="layout-select"
                value={selectedLayout?.id || ''}
                onChange={(e) => {
                  const layout = layouts.find(l => l.id === e.target.value);
                  setSelectedLayout(layout || null);
                }}
              >
                <option value="">All Cameras</option>
                {layouts.map(layout => (
                  <option key={layout.id} value={layout.id}>{layout.name}</option>
                ))}
              </select>
            )}
            <span className="camera-count">{filteredCameras.length} camera{filteredCameras.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="camera-grid">
            {filteredCameras.map((camera) => (
              <div 
                key={camera.id} 
                className="camera-card"
                onClick={() => handleCameraSelect(camera)}
              >
                <div className="camera-thumbnail">
                  <LazyThumbnail cameraId={camera.id} alt={camera.name} />
                </div>
                <span className="camera-name">{camera.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Selected camera view with timeline
  return (
    <div className="archive-new">
      <div className="archive-player-view">
        {/* Header */}
        <div className={`archive-header ${showControls ? 'visible' : 'hidden'}`}>
          <button className="archive-back-btn" onClick={handleCloseCamera}>
            <ChevronLeft size={24} />
          </button>
          <span className="archive-camera-name">{selectedCamera.name}</span>
          <button 
            className={`archive-live-btn ${isLiveMode ? 'active' : ''}`} 
            onClick={goToLive}
          >
            <span className={`status-dot ${isLiveMode ? 'live' : 'recorded'}`} />
            {isLiveMode ? 'LIVE' : 'RECORDED'}
          </button>
          {selectedCamera?.integrationType === 'GENETEC_VMS' && genetecStreamConfig && (
            <PtzControls
              integrationId={genetecStreamConfig.integrationId}
              cameraGuid={genetecStreamConfig.cameraGuid}
              integrationType={selectedCamera.integrationType}
            />
          )}
        </div>
        
        {/* Video area */}
        <div className="archive-video-wrapper" onClick={handleVideoTap}>
          {/* Video controls overlay - inside wrapper */}
          <div className={`archive-controls-overlay ${showControls ? 'visible' : 'hidden'}`} onClick={(e) => e.stopPropagation()}>
            <button className="archive-control-btn" onClick={handlePlayPause}>
              {isPlaying ? <Pause size={28} /> : <Play size={28} />}
            </button>
            <button className="archive-control-btn" onClick={() => setIsMuted(!isMuted)}>
              {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            </button>

          </div>
          <DigitalZoom>
          {/* No recording overlay */}
          {isPlaying && !isLiveMode && !hasRecordingAtPlayback && recordings.length > 0 && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)',
              color: 'white', zIndex: 15, gap: '0.75rem'
            }}>
              <VideoOff size={36} style={{ opacity: 0.6 }} />
              <span style={{ fontSize: '1rem', opacity: 0.8 }}>No recording at this time</span>
              <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>{format(playbackTime, 'MMM d, yyyy  HH:mm:ss')}</span>
              <button 
                onClick={(e) => { e.stopPropagation(); jumpToNearestRecording(); }}
                style={{
                  marginTop: '0.5rem', padding: '0.5rem 1rem', borderRadius: '6px',
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                  color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontSize: '0.85rem', transition: 'background 0.2s'
                }}
                onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.15)'}
              >
                <SkipForward size={16} />
                Jump to nearest recording
              </button>
            </div>
          )}
          {!isPlaying ? (
            <div className="archive-thumbnail-container">
              <img 
                src={`/api/vms/cameras/${encodeURIComponent(selectedCamera.id)}/thumbnail?tenantId=${localStorage.getItem("currentTenantId")}`}
                alt={selectedCamera.name}
                className="archive-thumbnail-img"
              />
              <div className="archive-thumbnail-overlay">
                <button className="archive-play-big-btn" onClick={() => setIsPlaying(true)}>
                  <Play size={48} />
                </button>
                <span className="archive-thumbnail-label">
                  {isLiveMode ? 'Click to view live' : `Play from ${format(playbackTime, 'HH:mm:ss')}`}
                </span>
              </div>
            </div>
          ) : selectedCamera?.integrationType === 'GENETEC_VMS' && genetecStreamConfig ? (
            <GenetecPlayer
              key={'genetec-' + selectedCamera.id}
              cameraGuid={genetecStreamConfig.cameraGuid}
              integrationId={genetecStreamConfig.integrationId}
              mediaGatewayEndpoint={genetecStreamConfig.mediaGatewayEndpoint}
              gwpJsUrl={genetecStreamConfig.gwpJsUrl}
              tokenEndpoint={genetecStreamConfig.tokenEndpoint}
              autoPlay={true}
              isLiveMode={isLiveMode}
              playbackTime={isLiveMode ? null : playbackTime}
              muted={isMuted}
              onError={(msg) => console.error('[Cameras] Genetec error:', msg)}
            />
          ) : selectedCamera?.vmsType === 'MILESTONE_VMS' ? (
            <MilestonePlayer
              key={`milestone-${selectedCamera.id}-${isLiveMode ? "live" : "archive"}`}
              camera={selectedCamera}
              integrationId={selectedCamera.integrationId}
              positionMs={isLiveMode ? null : playbackTime.getTime()}
            />
          ) : selectedCamera?.metadata?.vmsType === 'mirasys' ? (
            <MirasysPlayer
              key={`mirasys-${selectedCamera.id}-${isLiveMode ? "live" : "archive"}`}
              cameraId={selectedCamera.id}
              positionMs={isLiveMode ? null : playbackTime.getTime()}
              muted={isMuted}
              className="w-full h-full"
              
              
            />
          ) : (
            <WebRTCPlayer
              camera={selectedCamera}
              stream="primary"
              positionMs={isLiveMode ? null : playbackTime.getTime()}
              muted={isMuted}
              onFallback={() => console.log('Stream fallback')}
            />
          )}
          </DigitalZoom>
        </div>
        
      </div>
      
      {/* Timeline section */}
      <div className={`archive-timeline-section ${showControls ? 'visible' : 'hidden'}`} style={{ position: 'relative', zIndex: 30 }} onClick={(e) => e.stopPropagation()} onMouseEnter={handleTimelineInteraction} onMouseMove={handleTimelineInteraction}>
        <div className="archive-time-display">
          <button className="date-nav-btn" onClick={() => setSelectedDate(prev => subDays(prev, 1))}>
            <ChevronLeft size={20} />
          </button>
          <button 
            className="current-date-btn" 
            onClick={() => { setShowDatePicker(!showDatePicker); setShowTimePicker(false); }}
          >
            <Calendar size={16} />
            <span>{format(selectedDate, 'MM/dd')}</span>
          </button>
          <button 
            className="date-nav-btn" 
            onClick={() => setSelectedDate(prev => addDays(prev, 1) <= new Date() ? addDays(prev, 1) : prev)}
          >
            <ChevronRight size={20} />
          </button>
          <button 
            className="current-time-btn" 
            onClick={() => { setShowTimePicker(!showTimePicker); setShowDatePicker(false); }}
          >
            {formatTimeDisplay(playbackTime)}
          </button>
          

          
          <div className="timeline-zoom">
            <button className={`zoom-btn ${zoomLevel <= 0.0083 ? 'active' : ''}`} onClick={() => setZoomLevel(0.0083)}>30s</button>
            <button className={`zoom-btn ${zoomLevel > 0.0083 && zoomLevel <= 0.0833 ? 'active' : ''}`} onClick={() => setZoomLevel(0.0833)}>5m</button>
            <button className={`zoom-btn ${zoomLevel > 0.0833 && zoomLevel <= 0.25 ? 'active' : ''}`} onClick={() => setZoomLevel(0.25)}>30m</button>
            <button className={`zoom-btn ${zoomLevel > 0.25 ? 'active' : ''}`} onClick={() => setZoomLevel(1)}>2h</button>
          </div>
          
          <div className="timeline-filters">
            <button 
              className={`filter-btn recordings ${filters.recordings ? 'active' : ''}`}
              onClick={() => setFilters(f => ({...f, recordings: !f.recordings}))}
            >
              <span className="filter-dot"></span>REC
            </button>
            <button 
              className={`filter-btn motion ${filters.motion ? 'active' : ''}`}
              onClick={() => setFilters(f => ({...f, motion: !f.motion}))}
            >
              <span className="filter-dot"></span>MOT
            </button>
            <button 
              className={`filter-btn analytics ${filters.analytics ? 'active' : ''}`}
              onClick={() => setFilters(f => ({...f, analytics: !f.analytics}))}
            >
              <span className="filter-dot"></span>AI
            </button>
          </div>
          
          <div className="quick-jump-buttons">
            <button className="quick-jump-btn" onClick={() => goToLive()}>NOW</button>
            <button className="quick-jump-btn" onClick={() => goToRecorded(subHours(new Date(), 1))}>-1 HOUR</button>
            <button className="quick-jump-btn" onClick={() => goToRecorded(subHours(new Date(), 24))}>-24 HOURS</button>
            <button className="quick-jump-btn" onClick={() => goToRecorded(subDays(new Date(), 7))}>-7 DAYS</button>
          </div>
          
          {showDatePicker && (
            <CalendarPicker
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                const newDate = new Date(date);
                newDate.setHours(playbackTime.getHours(), playbackTime.getMinutes(), playbackTime.getSeconds());
                goToRecorded(newDate);
                setShowDatePicker(false);
              }}
              onClose={() => setShowDatePicker(false)}
              maxDate={new Date()}
            />
          )}
          
          {showTimePicker && (
            <TimePicker
              selectedTime={playbackTime}
              onTimeSelect={(time) => {
                goToRecorded(time);
                setShowTimePicker(false);
              }}
              onClose={() => setShowTimePicker(false)}
            />
          )}
        </div>
        
        {/* Timeline */}
        <div 
          className="archive-timeline" 
          ref={timelineRef} 
          onMouseDown={handleTimelineMouseDown} 
          onTouchStart={handleTimelineMouseDown}
        >
          <div className="timeline-markers">
            {generateTimeMarkers().map((marker, idx) => (
              <div 
                key={idx} 
                className={`timeline-marker ${marker.isHour ? 'hour' : marker.isMajor ? 'major' : ''}`} 
                style={{ transform: `translateX(${marker.offset}px)` }}
              >
                <span className="marker-label">
                  {zoomLevel <= 0.0833 ? format(marker.time, 'mm:ss') : marker.isHour ? format(marker.time, 'HH:mm') : format(marker.time, ':mm')}
                </span>
                <div className="marker-tick" />
              </div>
            ))}
          </div>
          <div className="timeline-activity">
            {generateActivityBars().map((bar) => (
              <div 
                key={bar.key} 
                className={`activity-bar ${bar.type}`} 
                style={{ 
                  transform: `translateX(${bar.offset}px)`, 
                  width: `${bar.width}px`
                }} 
              />
            ))}
          </div>
          <div className="timeline-playhead">
            <div className="playhead-line" />
            <div className="playhead-arrow" />
          </div>
        </div>
        
        {!loading && recordings.length === 0 && selectedCamera && (
          <div className="timeline-no-recordings" style={{ pointerEvents: 'none' }}>No recordings found for this date</div>
        )}
      </div>
    </div>
  );
};

export default ArchivePlaybackNew;
